import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '@/styles/neon-data-grid.css';
import './clusters-and-pathways.grid.css';
import {
  NeonButton,
  NeonCardStandard,
  NeonErrorPageContent,
  NeonPagination,
  NeonSearch,
  NeonTooltip,
} from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import { EMPTY_STATE_CARD_STYLE } from '@/components/EmptyState/empty-state.styles';
import type { ColDef, CellStyle } from 'ag-grid-community';
import type { FilterChip, FilterField } from '@/components/DataGrid/filter-panel';
import { DataGrid } from '@/components/DataGrid/data-grid';
import { NoFilterResults } from '@/components/EmptyState/no-filter-results';
import { TableSkeleton } from '@/components/TableSkeleton/table-skeleton';
import { usePageConfig } from '@/layout-templates';
import { useGetClusters } from '@/repositories/clusters-repository';
import { useGetSchools } from '@/repositories/school-repository';
import type { ClusterListItem } from '@/types/clusters';
import { isPosReadOnly } from '@/utils/cp-context';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import {
  CLUSTERS_PAGE_SIZE_OPTIONS,
  DEFAULT_CLUSTERS_PAGE_SIZE,
  EMPTY_SPECIALIZATION_AREAS_TEXT,
  SPECIALIZATION_AREAS_SKELETON_COLUMNS,
  SPECIALIZATION_AREAS_GRID_HEADER_CLASS,
  SPECIALIZATION_AREAS_GRID_HEADER_CLASS_RIGHT,
  SPECIALIZATION_AREAS_ICON,
  SPECIALIZATION_AREAS_ID,
  SPECIALIZATION_AREAS_TEXT,
  getSpecializationAreasBreadcrumbs,
} from '@/constants/clusters-pathway-constants';
import { ClusterPublishDrawer } from './cluster-publish-drawer';
import {
  actionButtonsContainerStyle,
  cellTextStyle,
  linkCellStyle,
  searchFieldStyle,
  toolbarCountStyle,
} from './clusters-and-pathways.styles';

const FILTER_STORAGE_KEY = 'specialization-areas-list-filters';

const DEFAULT_FILTER: { hideNoActiveSpecializations: boolean; schools: string[] } = {
  hideNoActiveSpecializations: true,
  schools: [],
};

type AppliedClusterFilters = {
  search: string;
  hideNoActiveSpecializations: boolean;
  schools: string[];
};

const DEFAULT_APPLIED: AppliedClusterFilters = {
  search: '',
  hideNoActiveSpecializations: true,
  schools: [],
};

type PersistedClustersListState = AppliedClusterFilters & {
  page: number;
  pageSize: number;
};

function loadPersistedListState(): PersistedClustersListState | null {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as PersistedClustersListState;
  } catch {
    return null;
  }
}

function persistListState(state: PersistedClustersListState): void {
  try {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function stringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

function countSelectedFilters(filters: {
  hideNoActiveSpecializations: boolean;
  schools: string[];
}): number {
  return filters.schools.length + (filters.hideNoActiveSpecializations ? 1 : 0);
}

function normalizeClustersPageSize(pageSize: number | undefined): number {
  const allowedPageSizes = CLUSTERS_PAGE_SIZE_OPTIONS.split(',').map((value) => Number(value));
  return pageSize !== undefined && allowedPageSizes.includes(pageSize)
    ? pageSize
    : DEFAULT_CLUSTERS_PAGE_SIZE;
}

export function ClustersAndPathways(): ReactElement {
  const navigate = useNavigate();
  const isDistrictStaff = !isPosReadOnly();
  const persistedState = useMemo(() => loadPersistedListState(), []);
  const initialAppliedFilters = useMemo<AppliedClusterFilters>(
    () => ({
      search: persistedState?.search ?? DEFAULT_APPLIED.search,
      hideNoActiveSpecializations:
        persistedState?.hideNoActiveSpecializations ?? DEFAULT_APPLIED.hideNoActiveSpecializations,
      schools: isDistrictStaff ? (persistedState?.schools ?? DEFAULT_APPLIED.schools) : [],
    }),
    [isDistrictStaff, persistedState]
  );
  const [draftFilters, setDraftFilters] = useState(() => ({
    hideNoActiveSpecializations: initialAppliedFilters.hideNoActiveSpecializations,
    schools: initialAppliedFilters.schools,
  }));
  const [appliedFilters, setAppliedFilters] =
    useState<AppliedClusterFilters>(initialAppliedFilters);
  const [currentPage, setCurrentPage] = useState(() => persistedState?.page ?? 1);
  const [pageLength, setPageLength] = useState(() =>
    normalizeClustersPageSize(persistedState?.pageSize)
  );
  const [searchValue, setSearchValue] = useState(initialAppliedFilters.search);
  const [searchKey, setSearchKey] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [gridReady, setGridReady] = useState(false);
  const [publishedSchoolsDrawerRow, setPublishedSchoolsDrawerRow] = useState<
    ClusterListItem | undefined
  >();
  const gridReadyRef = useRef(false);
  const { data: schools = [] } = useGetSchools();
  const sortedSchools = useMemo(
    () => [...schools].sort((a, b) => (a.name > b.name ? 1 : -1)),
    [schools]
  );

  const {
    data: clustersData,
    isFetching,
    isError,
    refetch,
  } = useGetClusters({
    page: currentPage,
    pageSize: pageLength,
    search: appliedFilters.search,
    schools: appliedFilters.schools,
    hideNoActiveSpecializations: appliedFilters.hideNoActiveSpecializations,
  });

  const rows = isApplyingFilters ? [] : (clustersData?.data ?? []);
  const totalClusters = clustersData?.total ?? 0;
  const showSkeleton = !isError && (!clustersData || (rows.length > 0 && !gridReady));

  useEffect(() => {
    if (isApplyingFilters && !isFetching) {
      setIsApplyingFilters(false);
    }
  }, [isApplyingFilters, isFetching]);

  useEffect(() => {
    persistListState({
      ...appliedFilters,
      schools: isDistrictStaff ? appliedFilters.schools : [],
      page: currentPage,
      pageSize: pageLength,
    });
  }, [appliedFilters, currentPage, isDistrictStaff, pageLength]);

  const onCreate = useCallback((): void => {
    navigate(`/${STAFF_ROUTES.clusterAndPathway.create}`);
  }, [navigate]);

  usePageConfig({
    breadcrumbs: getSpecializationAreasBreadcrumbs,
    breadcrumbsId: SPECIALIZATION_AREAS_ID.Breadcrumbs,
    title: SPECIALIZATION_AREAS_TEXT.Title,
    action: !isPosReadOnly()
      ? {
          id: SPECIALIZATION_AREAS_ID.Button,
          text: SPECIALIZATION_AREAS_TEXT.Button,
          icon: SPECIALIZATION_AREAS_ICON.Add,
          type: NeonButtonTypes.Primary,
          onClick: onCreate,
        }
      : undefined,
  });

  const schoolOptions = useMemo(
    () =>
      sortedSchools.map((s) => ({
        id: s.nid,
        name: s.nid,
        labelText: s.name,
      })),
    [sortedSchools]
  );

  const handleSearchChange = useCallback((value: string): void => {
    setSearchValue(value);
    setIsApplyingFilters(true);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      search: value,
    }));
  }, []);

  const hasFilterChanges = useMemo(
    () =>
      !stringArraysEqual(draftFilters.schools, appliedFilters.schools) ||
      draftFilters.hideNoActiveSpecializations !== appliedFilters.hideNoActiveSpecializations,
    [draftFilters, appliedFilters]
  );

  const handleFilterApply = useCallback((): void => {
    setIsApplyingFilters(true);
    setCurrentPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      schools: isDistrictStaff ? draftFilters.schools : [],
      hideNoActiveSpecializations: draftFilters.hideNoActiveSpecializations,
    }));
  }, [draftFilters.schools, draftFilters.hideNoActiveSpecializations, isDistrictStaff]);

  const handleFilterFieldChange = useCallback((fieldId: string, value: unknown): void => {
    setDraftFilters((prev) => {
      if (fieldId === 'hideNoActiveSpecializations') {
        return { ...prev, hideNoActiveSpecializations: value as boolean };
      }
      if (fieldId === 'schools') {
        return { ...prev, schools: Array.isArray(value) ? (value as string[]) : [] };
      }
      return prev;
    });
  }, []);

  const appliedFilterChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    if (appliedFilters.schools.length > 0) {
      for (const schoolId of appliedFilters.schools) {
        const school = schoolOptions.find((o) => o.id === schoolId);
        if (!school) {
          continue;
        }
        chips.push({
          id: `school:${schoolId}`,
          label: `${SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn} | ${school.labelText}`,
        });
      }
    }
    return chips;
  }, [appliedFilters.schools, schoolOptions]);

  const draftFilterChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];
    if (draftFilters.schools.length > 0) {
      for (const schoolId of draftFilters.schools) {
        const school = schoolOptions.find((o) => o.id === schoolId);
        if (!school) {
          continue;
        }
        chips.push({
          id: `school:${schoolId}`,
          label: `${SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn} | ${school.labelText}`,
        });
      }
    }
    return chips;
  }, [draftFilters.schools, schoolOptions]);

  const visibleFilterChips = isFilterOpen ? draftFilterChips : appliedFilterChips;

  const visibleFilterCount = useMemo(
    () => countSelectedFilters(isFilterOpen ? draftFilters : appliedFilters),
    [appliedFilters, draftFilters, isFilterOpen]
  );

  const handleChipRemove = useCallback(
    (chipId: string): void => {
      if (chipId.startsWith('school:')) {
        const schoolId = chipId.slice('school:'.length);
        setDraftFilters((p) => ({ ...p, schools: p.schools.filter((id) => id !== schoolId) }));
        if (!isFilterOpen) {
          setIsApplyingFilters(true);
          setCurrentPage(1);
          setAppliedFilters((prev) => ({
            ...prev,
            schools: prev.schools.filter((id) => id !== schoolId),
          }));
        }
      }
    },
    [isFilterOpen]
  );

  const handleClearAllChips = useCallback((): void => {
    setDraftFilters({ ...DEFAULT_FILTER });
    if (!isFilterOpen) {
      setIsApplyingFilters(true);
      setCurrentPage(1);
      setAppliedFilters((prev) => ({
        ...prev,
        schools: [],
        hideNoActiveSpecializations: true,
      }));
    }
  }, [isFilterOpen]);

  const handleClearFiltersAndApply = useCallback((): void => {
    setDraftFilters({ ...DEFAULT_FILTER });
    setCurrentPage(1);
    setPageLength(DEFAULT_CLUSTERS_PAGE_SIZE);
    setAppliedFilters({ ...DEFAULT_APPLIED, search: '' });
    setSearchValue('');
    setSearchKey((k) => k + 1);
  }, []);

  /** Same idea as Subject areas: failed GET must not look like an empty namespace (`total === 0`). */
  const listLoadErrorState = useMemo(
    () => (
      <NeonCardStandard dataInjectionSpacing="false">
        <div
          data-slot={DATA_SLOT}
          style={{
            ...EMPTY_STATE_CARD_STYLE,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <NeonErrorPageContent dataErrorCode="500" />
          <NeonButton
            id={SPECIALIZATION_AREAS_ID.ListLoadRetry}
            dataType={NeonButtonTypes.Primary}
            dataText={SPECIALIZATION_AREAS_TEXT.ListLoadRetry}
            onClick={(): void => {
              void refetch();
            }}
          />
        </div>
      </NeonCardStandard>
    ),
    [refetch]
  );

  const defaultFilterFields = useMemo<FilterField[]>(
    () => [
      {
        type: 'checkbox',
        id: 'hideNoActiveSpecializations',
        label: SPECIALIZATION_AREAS_TEXT.FilterHideNoActiveLabel,
        checked: true,
      },
      ...(isDistrictStaff
        ? [
            {
              type: 'multiselect' as const,
              id: 'schools',
              label: SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn,
              value: [],
              placeholder: 'Select schools',
              options: schoolOptions,
              helperText: SPECIALIZATION_AREAS_TEXT.FilterPublishedSchoolsHelper,
            },
          ]
        : []),
    ],
    [isDistrictStaff, schoolOptions]
  );

  const filterFields = useMemo<FilterField[]>(
    () => [
      {
        type: 'checkbox',
        id: 'hideNoActiveSpecializations',
        label: SPECIALIZATION_AREAS_TEXT.FilterHideNoActiveLabel,
        checked: draftFilters.hideNoActiveSpecializations,
      },
      ...(isDistrictStaff
        ? [
            {
              type: 'multiselect' as const,
              id: 'schools',
              label: SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn,
              value: draftFilters.schools,
              placeholder: 'Select schools',
              options: schoolOptions,
              helperText: SPECIALIZATION_AREAS_TEXT.FilterPublishedSchoolsHelper,
            },
          ]
        : []),
    ],
    [draftFilters, isDistrictStaff, schoolOptions]
  );

  const handleFirstDataRendered = useCallback(() => {
    if (!gridReadyRef.current) {
      gridReadyRef.current = true;
      setGridReady(true);
    }
  }, []);

  const baseCellStyle: CellStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      padding: '0 0.5rem',
    }),
    []
  );

  const columnDefs = useMemo<ColDef<ClusterListItem>[]>(() => {
    const columns: ColDef<ClusterListItem>[] = [
      {
        field: 'name',
        headerName: SPECIALIZATION_AREAS_TEXT.AreaColumn,
        flex: 1,
        minWidth: 180,
        headerClass: SPECIALIZATION_AREAS_GRID_HEADER_CLASS,
        cellStyle: { ...baseCellStyle, justifyContent: 'flex-start' },
        sortable: false,
        cellRenderer: (props: { data: ClusterListItem }) => {
          const row = props.data;
          const detailKey = row?.id?.trim() || row?.name?.trim();
          if (!detailKey) {
            return <span>{row?.name}</span>;
          }
          return (
            <span
              role="link"
              tabIndex={0}
              style={{ ...linkCellStyle, cursor: 'pointer' }}
              onClick={() =>
                navigate(`/${STAFF_ROUTES.clusterAndPathway.detail(detailKey)}`, {
                  state: {
                    areaName: row.name,
                    description: row.description,
                  },
                })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/${STAFF_ROUTES.clusterAndPathway.detail(detailKey)}`, {
                    state: {
                      areaName: row.name,
                      description: row.description,
                    },
                  });
                }
              }}
            >
              {row.name}
            </span>
          );
        },
      },
      {
        colId: 'specializations',
        headerName: SPECIALIZATION_AREAS_TEXT.SpecializationsColumn,
        flex: 1.35,
        minWidth: 220,
        headerClass: SPECIALIZATION_AREAS_GRID_HEADER_CLASS,
        cellStyle: { ...baseCellStyle, justifyContent: 'flex-start' },
        sortable: false,
        valueGetter: (p) => p.data?.specializationSummary,
        cellRenderer: (props: { data: ClusterListItem }) => {
          const row = props.data;
          const text = row?.specializationSummary?.trim() ?? '';
          if (!text) {
            const areaLabel = row?.name?.trim() || row?.id?.trim() || 'this area';
            return (
              <span
                role="img"
                title="No specializations"
                aria-label={`No specializations for ${areaLabel}`}
                style={cellTextStyle}
              >
                —
              </span>
            );
          }
          return <span style={cellTextStyle}>{text}</span>;
        },
      },
      {
        colId: 'publishedSchools',
        headerName: SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn,
        flex: 1,
        width: 190,
        minWidth: 160,
        headerClass: `${SPECIALIZATION_AREAS_GRID_HEADER_CLASS_RIGHT} published-schools-spaced-header published-schools-center-header`,
        cellClass: 'published-schools-spaced-cell',
        cellStyle: { ...baseCellStyle, justifyContent: 'center' },
        sortable: false,
        headerComponent: () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              gap: '0.125rem',
            }}
          >
            <span className="ag-header-cell-text">
              {SPECIALIZATION_AREAS_TEXT.PublishedSchoolsColumn}
            </span>
            <NeonButton
              id={SPECIALIZATION_AREAS_ID.PublishedSchoolsInfoTooltip}
              dataText="Published schools info"
              dataShowText="false"
              dataType={NeonButtonTypes.Utility}
              dataIcon="circle-info"
              dataAriaLabel="Published schools information"
            />
            <NeonTooltip dataTargetId={SPECIALIZATION_AREAS_ID.PublishedSchoolsInfoTooltip}>
              {SPECIALIZATION_AREAS_TEXT.PublishedSchoolsTooltip}
            </NeonTooltip>
          </div>
        ),
        cellRenderer: (props: { data: ClusterListItem }) => {
          const count = props.data.publishedSchools?.length ?? 0;
          const hasSpecializations = Boolean(props.data.specializationSummary?.trim());
          if (count === 0 && !hasSpecializations) {
            return <span style={cellTextStyle}>{count}</span>;
          }
          return (
            <span
              style={linkCellStyle}
              role="button"
              tabIndex={0}
              aria-label={`Open published schools for ${props.data.name}`}
              onClick={() => setPublishedSchoolsDrawerRow(props.data)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPublishedSchoolsDrawerRow(props.data);
                }
              }}
            >
              {count}
            </span>
          );
        },
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        width: 112,
        minWidth: 112,
        headerClass: `${SPECIALIZATION_AREAS_GRID_HEADER_CLASS} actions-column-spaced-header actions-column-end-header`,
        cellClass: 'actions-column-spaced-cell',
        cellStyle: { ...baseCellStyle, justifyContent: 'center' },
        sortable: false,
        cellRenderer: (props: { data: ClusterListItem }) => {
          return (
            <div style={actionButtonsContainerStyle}>
              <NeonButton
                id={`edit-cluster-${props.data.id}`}
                dataIcon="edit"
                dataType="utility"
                dataAriaLabel={`Edit ${props.data.name}`}
                dataText="Edit"
                dataShowText="false"
                onClick={(): void => {
                  navigate(`/${STAFF_ROUTES.clusterAndPathway.edit(props.data.id)}`, {
                    state: { from: 'list' },
                  });
                }}
              />
              <NeonTooltip dataTargetId={`edit-cluster-${props.data.id}`}>Edit</NeonTooltip>
            </div>
          );
        },
      },
    ];

    if (!isDistrictStaff) {
      return columns.filter((column) => column.colId !== 'actions');
    }

    return columns;
  }, [baseCellStyle, isDistrictStaff, navigate]);

  const skeletonContent = useMemo(
    () => (
      <TableSkeleton
        columns={SPECIALIZATION_AREAS_SKELETON_COLUMNS}
        rowCount={6}
        testId="specialization-areas-skeleton"
        ariaLabel="Loading areas"
      />
    ),
    []
  );

  const cardSkeletonContent = useMemo(
    () => (
      <NeonCardStandard dataInjectionSpacing="false">
        <div data-slot={DATA_SLOT}>{skeletonContent}</div>
      </NeonCardStandard>
    ),
    [skeletonContent]
  );

  return (
    <>
      {showSkeleton && <div style={{ position: 'relative', zIndex: 1 }}>{skeletonContent}</div>}
      <div
        style={
          showSkeleton
            ? { position: 'absolute', left: '-9999px', visibility: 'hidden', width: '100%' }
            : undefined
        }
      >
        <DataGrid<ClusterListItem>
          id="specialization-areas"
          columnDefs={columnDefs}
          rowData={rows}
          domLayout="normal"
          gridHeight="min(600px, 70vh)"
          cardWrapper
          childrenInsideCard
          gridOptions={{
            onFirstDataRendered: handleFirstDataRendered,
            defaultColDef: { resizable: false },
          }}
          emptyState={
            isError ? (
              listLoadErrorState
            ) : isApplyingFilters ? (
              cardSkeletonContent
            ) : showSkeleton ? (
              <></>
            ) : appliedFilters.search?.trim() && totalClusters === 0 ? (
              <NoFilterResults
                title={EMPTY_SPECIALIZATION_AREAS_TEXT.NoFilterResultsTitle}
                body=""
                onClearFilters={handleClearFiltersAndApply}
                showAction={false}
              />
            ) : totalClusters === 0 ? (
              <NoFilterResults
                title={EMPTY_SPECIALIZATION_AREAS_TEXT.NoFilterResultsTitle}
                body=""
                onClearFilters={handleClearFiltersAndApply}
                showAction={false}
              />
            ) : undefined
          }
          emptyStateWhenFiltered={
            isError ? (
              listLoadErrorState
            ) : isApplyingFilters || showSkeleton ? (
              cardSkeletonContent
            ) : (
              <NoFilterResults
                title={EMPTY_SPECIALIZATION_AREAS_TEXT.NoFilterResultsTitle}
                body=""
                onClearFilters={handleClearFiltersAndApply}
                showAction={false}
              />
            )
          }
          emptyRowsTreatAsFiltered={
            Boolean(appliedFilters.search?.trim()) || appliedFilterChips.length > 0
          }
          className="neon-data-grid specialization-areas-grid"
          isFilterOpen={isFilterOpen}
          onFilterOpenChange={setIsFilterOpen}
          filter={{
            title: SPECIALIZATION_AREAS_TEXT.Filters,
            fields: filterFields,
            chips: visibleFilterChips,
            filterCount: visibleFilterCount,
            onFieldChange: handleFilterFieldChange,
            onChipRemove: handleChipRemove,
            onClearAllChips: handleClearAllChips,
            onApply: handleFilterApply,
            hasChanges: hasFilterChanges && !isApplyingFilters,
            defaultFields: defaultFilterFields,
            applyText: SPECIALIZATION_AREAS_TEXT.FilterApply,
            closeButtonId: SPECIALIZATION_AREAS_ID.FilterCancelButton,
            applyButtonId: SPECIALIZATION_AREAS_ID.FilterApplyButton,
            toggleButtonId: SPECIALIZATION_AREAS_ID.FiltersButton,
            toggleButtonText: SPECIALIZATION_AREAS_TEXT.Filters,
            toggleButtonIcon: SPECIALIZATION_AREAS_ICON.Filters,
            hideToggleButton: false,
            hideActionBar: false,
          }}
          actionBar={
            totalClusters > 0 ? (
              <>
                <span style={toolbarCountStyle}>
                  {totalClusters} {totalClusters === 1 ? 'area' : 'areas'}
                </span>
                <div style={searchFieldStyle}>
                  <NeonSearch
                    key={searchKey}
                    id={SPECIALIZATION_AREAS_ID.SearchField}
                    dataPlaceholderText={SPECIALIZATION_AREAS_TEXT.SearchPlaceholder}
                    value={searchValue}
                    neonSearchValueChange={handleSearchChange}
                  />
                </div>
              </>
            ) : null
          }
        >
          {!isApplyingFilters && totalClusters > 0 && (
            <form onSubmit={(e) => e.preventDefault()} style={{ display: 'contents' }}>
              <div onClickCapture={(e) => e.preventDefault()}>
                <NeonPagination
                  id="specialization-areas-pagination"
                  dataArrayLength={totalClusters}
                  dataCurrentPage={currentPage}
                  dataPageLength={pageLength}
                  dataPageSizeOptions={CLUSTERS_PAGE_SIZE_OPTIONS}
                  dataShowJumpTo
                  dataShowResults
                  neonPaginationChange={(newPage, newPageLength) => {
                    setCurrentPage(newPage);
                    setPageLength(newPageLength);
                  }}
                />
              </div>
            </form>
          )}
        </DataGrid>
      </div>
      {publishedSchoolsDrawerRow ? (
        <ClusterPublishDrawer
          clusterId={publishedSchoolsDrawerRow.id}
          clusterName={publishedSchoolsDrawerRow.name}
          cluster={publishedSchoolsDrawerRow}
          isOpen={true}
          onClose={() => setPublishedSchoolsDrawerRow(undefined)}
          drawerId="specialization-areas-published-schools-drawer"
          disablePublish={!isDistrictStaff}
        />
      ) : null}
    </>
  );
}
